// Shared report data helpers for customer report hooks and internal workbenches.

// Flat-key names for each report category (matches server-side CATEGORY_TO_FIELD).
export const CATEGORY_FIELD_MAP = {
  summary: 'summaryReport',
  goals: 'goalsReport',
  zoning: 'zoningReport',
  site: 'siteReport',
  design: 'designReport',
  economics: 'economicsReport',
  timeline: 'timelineReport',
  impact: 'impactReport',
  actions: 'actionsReport',
};

const COMPLETE_STATUSES = new Set(['complete', 'completed', 'ready']);
const BLOCKED_STATUSES = new Set(['blocked', 'error', 'failed']);
const IN_FLIGHT_STATUSES = new Set(['queued', 'generating', 'in_progress', 'running', 'started']);
// Union of this file's IN_FLIGHT_STATUSES and useProjectReport's
// SELF_HEAL_IN_FLIGHT_OVERALL. Kept separate from IN_FLIGHT_STATUSES (which
// feeds getSynthesisReportActionState) so the honest-banner detection can also
// count backend lifecycle states like 'accepted'/'pending'/'processing'.
const ACTIVE_GENERATION_STATUSES = new Set([
  'accepted',
  'generating',
  'pending',
  'queued',
  'in_progress',
  'processing',
  'running',
  'started',
]);
const SECTION_METADATA_KEYS = new Set([
  'id',
  'category',
  'categoryId',
  'status',
  'error',
  'ui_specs',
  'version',
  'versionId',
  'reportId',
  'startedAt',
  'generatedAt',
  'completedAt',
  'attemptedAt',
  'createdAt',
  'updatedAt',
]);

const normalizeStatus = (status, fallback = 'missing') => {
  if (status == null || status === '') return fallback;
  return String(status).toLowerCase().replace(/\s+/g, '_');
};

const hasMeaningfulValue = (value) => {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

const countSectionFields = (section) => {
  if (!section || section.error) return 0;
  return Object.entries(section)
    .filter(([key, value]) => !SECTION_METADATA_KEYS.has(key) && hasMeaningfulValue(value))
    .length;
};

const hasCategoryPayload = (value) => {
  if (!value || value.error) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.entries(value)
      .some(([key, nestedValue]) => !SECTION_METADATA_KEYS.has(key) && hasMeaningfulValue(nestedValue));
  }
  return hasMeaningfulValue(value);
};

const extractCategoryStatus = (categoryStatus) => {
  if (!categoryStatus) return null;
  if (typeof categoryStatus === 'string') return categoryStatus;
  return categoryStatus.status
    || categoryStatus.phase
    || categoryStatus.overall
    || null;
};

/**
 * Compose the flat-key report shape from a baseline scalar doc + a map of
 * section docs keyed by category id ("summary", "zoning", ...). Sections
 * always win; baseline supplies timestamps, status map, patch metadata.
 */
export function composeReportFromSections(baseline, sectionsMap) {
  const composed = { ...(baseline || {}) };
  for (const [category, fieldName] of Object.entries(CATEGORY_FIELD_MAP)) {
    if (sectionsMap?.[category] !== undefined) {
      composed[fieldName] = sectionsMap[category];
    }
  }
  return composed;
}

/**
 * Transform a raw (flat-key) report object into the categories-keyed
 * reportData shape consumed by report UI renderers.
 */
export function transformReportDoc(reportDoc = {}, patchMeta = null) {
  const activeReportId = reportDoc.activeReportId || reportDoc.id || null;
  return {
    id: reportDoc.id || reportDoc.version || null,
    activeReportId,
    readableReportId: reportDoc.readableReportId || null,
    projectLocationIdentity: reportDoc.projectLocationIdentity || null,
    categories: {
      summary: reportDoc.summaryReport || null,
      zoning: reportDoc.zoningReport || null,
      site: reportDoc.siteReport || null,
      design: reportDoc.designReport || null,
      economics: reportDoc.economicsReport || null,
      timeline: reportDoc.timelineReport || null,
      impact: reportDoc.impactReport || null,
      goals: reportDoc.goalsReport || null,
      actions: reportDoc.actionsReport || null,
    },
    status: reportDoc.status || 'completed',
    reportStatus: reportDoc.reportStatus || null,
    categoryStatus: reportDoc.categoryStatus || null,
    generationStartedAt: reportDoc.generatedAt,
    generationCompletedAt: reportDoc.completedAt,
    // Baseline top-level fields carried through read-only for the facts-improvement
    // mirror classifier (Task 8). generatedAt is the report's original synthesis
    // timestamp (distinct from generationStartedAt's alias); sourceSnapshots.propertyFacts
    // is the property-facts snapshot the report was built on. Both are consumed by
    // classifyReportFactsImprovement and must match the raw reports_v1 doc shape.
    generatedAt: reportDoc.generatedAt ?? null,
    sourceSnapshots: reportDoc.sourceSnapshots || null,
    questionnaireSnapshot: reportDoc.questionnaireSnapshot || null,
    projectSynthesisContract: reportDoc.projectSynthesisContract || null,
    projectSynthesisContractId:
      reportDoc.projectSynthesisContractId || reportDoc.projectSynthesisContract?.contractId || null,
    projectSynthesisContractVersion:
      reportDoc.projectSynthesisContractVersion || reportDoc.projectSynthesisContract?.version || null,
    projectSynthesisSourceVersionId:
      reportDoc.projectSynthesisSourceVersionId || reportDoc.projectSynthesisContract?.sourceVersionId || null,
    version: reportDoc.version,
    tier: reportDoc.tier,
    patchVersion: patchMeta?.patchVersion || reportDoc._patchVersion || 0,
    lastPatchAt: patchMeta?.lastPatchAt || reportDoc._lastPatchAt || null,
    patchCount: patchMeta?.patchCount || reportDoc._patchCount || 0,
    isMaterialized: !!patchMeta?.isMaterialized,
  };
}

export function deriveReportVersionCategoryProgress(reportDoc = {}) {
  const categories = Object.keys(CATEGORY_FIELD_MAP);
  const filled = categories.filter((category) => {
    const fieldName = CATEGORY_FIELD_MAP[category];
    const hasExplicitPayload = Object.prototype.hasOwnProperty.call(reportDoc, fieldName);
    if (hasCategoryPayload(reportDoc[fieldName])) return true;

    // If a report version explicitly carries a category payload, trust the
    // payload over optimistic status maps. Missing payloads may still be
    // materialized as section docs, but empty/error payloads are not complete.
    if (hasExplicitPayload) return false;

    const status = normalizeStatus(reportDoc.status?.[category], null);
    if (COMPLETE_STATUSES.has(status)) return true;

    const categoryStatus = normalizeStatus(extractCategoryStatus(reportDoc.categoryStatus?.[category]), null);
    return COMPLETE_STATUSES.has(categoryStatus);
  }).length;

  return { filled, total: categories.length };
}

export function buildReportDataFromSections(baseline, sectionsMap, patchMeta = null) {
  return transformReportDoc(composeReportFromSections(baseline, sectionsMap), {
    patchVersion: baseline?._patchVersion || 0,
    lastPatchAt: baseline?._lastPatchAt || null,
    patchCount: baseline?._patchCount || 0,
    isMaterialized: Object.keys(sectionsMap || {}).length > 0,
    ...(patchMeta || {}),
  });
}

export function deriveReportSectionState({ category, report, section }) {
  const baselineStatus = typeof report?.status === 'object'
    ? report.status?.[category]
    : null;
  const categoryStatus = extractCategoryStatus(report?.categoryStatus?.[category]);
  const sectionStatus = section?.status;
  const candidates = [sectionStatus, categoryStatus, baselineStatus].map(status => normalizeStatus(status, null));
  const hasError = Boolean(section?.error || report?.categoryStatus?.[category]?.error);

  let status = hasError ? 'blocked' : null;
  if (!status && candidates.some(candidate => BLOCKED_STATUSES.has(candidate))) {
    status = 'blocked';
  }
  if (!status) {
    status = normalizeStatus(categoryStatus || baselineStatus || sectionStatus, section ? 'present' : 'missing');
  }

  const fieldCount = countSectionFields(section);
  const complete = COMPLETE_STATUSES.has(status);
  const blocked = BLOCKED_STATUSES.has(status);
  const inFlight = IN_FLIGHT_STATUSES.has(status);

  return {
    status,
    fieldCount,
    complete,
    blocked,
    inFlight,
  };
}

export function getSynthesisReportActionState({
  projectId,
  intentReady,
  factsReady,
  activeReportId,
  activeReport,
  activeReportStatus,
  actionPending,
  sourceSnapshotStale = false,
}) {
  const status = normalizeStatus(activeReportStatus, activeReport ? 'unknown' : 'not_started');
  const missingReason = !projectId
    ? 'No project is bound.'
    : !intentReady
      ? 'Intent layer is not complete.'
      : !factsReady
        ? 'Factual layer is not complete.'
        : '';

  if (actionPending) {
    return { mode: 'pending', label: 'Starting...', disabled: true, reason: 'Report generation request is in flight.' };
  }

  if (IN_FLIGHT_STATUSES.has(status)) {
    return { mode: 'in_flight', label: 'Generating...', disabled: true, reason: 'A report generation is already running.' };
  }

  if (missingReason) {
    return {
      mode: activeReport && BLOCKED_STATUSES.has(status) ? 'retry' : 'generate',
      label: activeReport && BLOCKED_STATUSES.has(status) ? 'Retry report' : 'Generate report',
      disabled: true,
      reason: missingReason,
    };
  }

  if (activeReport && BLOCKED_STATUSES.has(status)) {
    return { mode: 'retry', label: 'Retry report', disabled: false, reason: 'Create a fresh report version from the same project inputs and facts.' };
  }

  if (activeReport && sourceSnapshotStale) {
    return { mode: 'regenerate', label: 'Regenerate report', disabled: false, reason: 'Create a fresh report version from the current Project Facts snapshot.' };
  }

  if (!activeReport) {
    return { mode: 'generate', label: 'Generate report', disabled: false, reason: 'Create the first report version for this diagnostic project.' };
  }

  return { mode: 'none', label: 'Report attached', disabled: true, reason: 'This project already has an attached report version.' };
}

/**
 * Positive evidence of an in-flight report generation, derived from the composed
 * reportData shape (see transformReportDoc). Returns true ONLY when a status
 * value actively signals generation:
 *  - status is a per-category map ({ summary: 'complete', zoning: 'generating' })
 *    → active if ANY category normalizes into ACTIVE_GENERATION_STATUSES
 *  - status is a legacy string → active if it normalizes into the set
 *  - reportStatus.overall normalizes into the set
 *
 * Contract: absence of report data is NOT evidence of generation
 * (GH #696 round-2: false "report is generating" banner). A missing / complete /
 * failed status map means NOT generating.
 */
export function isReportGenerationActive(reportData) {
  if (!reportData) return false;

  const status = reportData.status;
  if (status && typeof status === 'object' && !Array.isArray(status)) {
    const anyActive = Object.values(status)
      .some((value) => ACTIVE_GENERATION_STATUSES.has(normalizeStatus(value, null)));
    if (anyActive) return true;
  } else if (ACTIVE_GENERATION_STATUSES.has(normalizeStatus(status, null))) {
    return true;
  }

  if (ACTIVE_GENERATION_STATUSES.has(normalizeStatus(reportData.reportStatus?.overall, null))) {
    return true;
  }

  return false;
}
